/**
 * @packageDocumentation
 * @module      CorLogin.ts
 * @description Component to display a Login page.
 * @copyright   Copyright © 2025 Siemens Healthineers. All rights reserved.
 */
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { css, customElement, html, internalProperty, LitElement, property } from 'lit-element';
import './CorThemeSelector';
import './CorLogoBar';
import ValidationTracker from "./ValidationTracker";
import { universalStyles } from './CorMain';
import PropertyBindingHandler from './PropertyBindingHandler';
import { msLogIn, MSALComponent } from './MsalMain';
import { MSALredirectionStateMachine } from './MsalStateMachine';
import { CorAccessBar } from './CorAccessBar';
/** Component to display a Login page. */
let CorLogin = class CorLogin extends LitElement {
    /**
     * LitElement looks for a static property by this name. If it finds one, it applies these styles to the component.
     * See https://lit-element.polymer-project.org/guide/styles#expressions
     */
    static get styles() {
        return css `
          ${universalStyles}
          
          #content-wrapper {
            width: 100%;
            height: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-direction: column;
            gap: 10px;
          }

          #content {
            width: 480px;
            margin-top: -40px; // So it's centered on entire screen, accounting for the logo bar
          }

          .contentItem {
            text-align: center;
          }

          div.flexcolumn {
            width: 100%;
            display: flex;
            flex-direction: column;
            align-items: center;
          }

          div.flexcolumn > * {
            width: fit-content;
            margin-bottom: 6px;
          }

          #footer {
            margin-top: 32px;
            display: flex;
            align-items: center;
            justify-content: space-between;
          }

        `;
    }
    get pageTitle() {
        return CorAccessBar.getLabel();
    }
    /**
     * Because LitElement does not have two-way binding, we must copy input values to properties ourselves.
     * @param targetProperty The property in this class to which the user's input should be copied.
     */
    inputHelper(targetProperty) {
        const self = this;
        return function (e) {
            if (e.currentTarget instanceof Element) {
                PropertyBindingHandler.bindPropertyWithEventTargetValue(self, targetProperty, e.currentTarget);
            }
        };
    }
    showErrorModal(label, message, revert, type) {
        this.errorModalLabel = label;
        this.errorModalMessage = message;
        this.errorModalRevertFunc = revert;
        this.errorModalType = type || 'error';
        this.wantErrorModal = true;
    }
    /** Closes the error modal. */
    errorModalClosed() {
        this.errorModalMessage = ''; // Just to be safe.
        this.wantErrorModal = false;
        this.errorModalRevertFunc();
    }
    constructor() {
        super();
        this.validationTracker = new ValidationTracker();
        this.errorModalRevertFunc = () => {
        };
        this.errorModalLabel = '';
        this.errorModalType = 'error';
        this.errorModalMessage = '';
        this.wantErrorModal = false;
    }
    /** Renders the component as part of LitElement's lifecycle. */
    render() {
        if (this.loginResultCode == MSALredirectionStateMachine.states.login_failure) {
            let loginFailedDisplay;
            if (MSALComponent.hasVersionMismatch) {
                loginFailedDisplay = 'Login Failed.' + ' ' + 'Version mismatch detected.';
            }
            else {
                loginFailedDisplay = 'Login Failed.';
            }
            this.showErrorModal(loginFailedDisplay, 'Please contact the support.', () => {
                // Clear the error, but msalState would still be 'login_failure'.
                // On clicking 'Log In', the user would be redirected again, where if the authorization is successful, the msalState would go to 'loading' and then to 'login_success'.
                this.loginResultCode = MSALredirectionStateMachine.states.in_login;
            });
        }
        return html `
            <sh-card>
                <cor-logo-bar></cor-logo-bar>
                <div id="content-wrapper">
                    <div id="content">
                        <sh-text id="title" size="super-header" class="contentItem">${this.pageTitle}</sh-text>
                        <sh-text size='body-1' color="secondary" class="contentItem">Version: ${MSALComponent.sessionManagerVersion}</sh-text>
                        <div id="footer">
                            ${html `
                                <sh-text id="login-message" size='body-1' color="secondary" class="contentItem">
                                    Login with your Siemens Healthineers account
                                </sh-text>
                                `}
                        </div>
                    </div>
                    <div>
                        <sh-button id="login" class="contentItem" label="Log In"
                                            @click="${msLogIn}"
                                            ?disabled="${MSALComponent.hasVersionMismatch}">
                        </sh-button>
                    </div>
                </div>
                <sh-modal id="error-modal" label=${this.errorModalLabel}
                          modal-height='250px' modal-width='400px'
                          sticky
                          type=${this.errorModalType}
                          ?visible="${this.wantErrorModal}"
                          @modal-closed="${() => this.errorModalClosed()}"
                >
                    <sh-text>${this.errorModalMessage}</sh-text>
                    <sh-button slot="footer" label="Close"
                        @click="${() => this.errorModalClosed()}">
                    </sh-button>
                </sh-modal>
            </sh-card>
        `;
    }
};
__decorate([
    property()
], CorLogin.prototype, "loginResultCode", void 0);
__decorate([
    internalProperty()
], CorLogin.prototype, "pageTitle", null);
__decorate([
    internalProperty()
], CorLogin.prototype, "errorModalLabel", void 0);
__decorate([
    internalProperty()
], CorLogin.prototype, "errorModalType", void 0);
__decorate([
    internalProperty()
], CorLogin.prototype, "errorModalMessage", void 0);
__decorate([
    internalProperty()
], CorLogin.prototype, "wantErrorModal", void 0);
CorLogin = __decorate([
    customElement('cor-login')
], CorLogin);
export { CorLogin };
//# sourceMappingURL=CorLogin.js.map